import React, { useState, useEffect } from 'react'
import { PARTICIPANT_COLORS, hashString } from './participantColors.js'
import { BotIcon, ChatIcon } from './icons.jsx'
import './ContactDetail.css'

export const ContactDetail = ({ contact, isOnline, peerService, onStartChat, onDeleteRole }) => {
  const [fields, setFields] = useState([])
  const [values, setValues] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const color = PARTICIPANT_COLORS[hashString(contact.roleId) % PARTICIPANT_COLORS.length]

  useEffect(() => {
    if (!isOnline || !peerService) { setFields([]); setValues({}); setLoaded(false); return }
    setError('')
    setLoaded(false)
    peerService.getWorkerConfig(contact.roleId)
      .then(result => {
        setFields(result.fields || [])
        setValues(result.values || {})
        setLoaded(true)
      })
      .catch(err => setError(err.message || 'Failed to load config'))
  }, [contact.roleId, isOnline, peerService])

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError('')
    try {
      await peerService.updateWorkerConfig(contact.roleId, values)
    } catch (err) {
      setError(err.message || 'Failed to save config')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="contact-detail">
      <div className="cd-header">
        <div className="cd-avatar" style={{ background: `linear-gradient(135deg, ${color}22, ${color}11)` }}>
          <BotIcon size={28} color={isOnline ? color : 'var(--text-tertiary)'} />
          <span className={`cd-status ${isOnline ? 'online' : 'offline'}`} />
        </div>
        <div className="cd-info">
          <div className="cd-name">{contact.displayName}</div>
          {contact.typeName && contact.displayName !== contact.typeName && (
            <div className="cd-type">{contact.typeName}</div>
          )}
          <div className={`cd-status-text ${isOnline ? 'online' : 'offline'}`}>
            {isOnline ? 'Online' : 'Offline'}
          </div>
          {contact.description && <div className="cd-desc">{contact.description}</div>}
        </div>
      </div>

      <div className="cd-actions">
        {isOnline && (
          <button className="cd-btn cd-btn-primary" onClick={() => onStartChat(contact)}>
            <ChatIcon /> Start Chat
          </button>
        )}
        {!isOnline && onDeleteRole && (
          <button className="cd-btn cd-btn-danger" onClick={() => onDeleteRole(contact.roleId)}>
            Delete
          </button>
        )}
      </div>

      {isOnline && peerService && (
        <div className="cd-config">
          <div className="cd-config-title">Configuration</div>
          {!loaded && !error && <div className="cd-config-loading">Loading configuration...</div>}
          {loaded && fields.length > 0 && (
            <div className="cd-config-fields">
              {fields.map(f => (
                <div key={f.key} className="cd-field">
                  <label className="cd-field-label">{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea
                      className="cd-field-input cd-field-textarea"
                      value={values[f.key] || ''}
                      onChange={e => handleChange(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={3}
                    />
                  ) : (
                    <input
                      className="cd-field-input"
                      type={f.type}
                      value={values[f.key] ?? ''}
                      onChange={e => handleChange(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                      placeholder={f.placeholder}
                      step={f.step}
                    />
                  )}
                </div>
              ))}
              <div className="cd-config-footer">
                <button className="cd-btn cd-btn-primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                {error && <span className="cd-config-error">{error}</span>}
              </div>
            </div>
          )}
          {loaded && fields.length === 0 && (
            <div className="cd-config-loading">No configurable options</div>
          )}
          {error && !loaded && <div className="cd-config-error">{error}</div>}
        </div>
      )}
    </div>
  )
}
